import { useEffect, useState } from 'react';

/**
 * Matches the global mobile breakpoint used across the moderation UI.
 * Anything < 900 px is treated as "mobile" — phones in portrait and the
 * narrower tablets in portrait orientation. The desktop chrome (left
 * sidebar + topbar) starts at 900 px to leave room for the 220 px sidebar
 * plus a usable content column.
 */
export const MOBILE_BREAKPOINT_PX = 900;

const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`;

function initialMatch(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(MOBILE_QUERY).matches;
}

/**
 * React hook returning `true` when the viewport is at or below the
 * moderation UI's mobile breakpoint. Subscribes to `matchMedia` change
 * events so the layout re-renders if the user rotates the device or
 * resizes the browser window.
 *
 * Server-side / SSR-safe: returns `false` when `window` is missing so the
 * desktop shell renders first. Hydration corrects the value on mount.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(initialMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    // matchMedia change listener: some older Safaris only support the
    // legacy addListener API, so check before binding.
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  return isMobile;
}
